import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  FacilityAttendanceSettingsDto,
  PayPeriod,
  ShiftTemplate,
  UpdateFacilityAttendanceSettingsDto
} from '@umss/shared/data-access';
import { Observable } from 'rxjs';

export interface BillableRate {
  scope: string;
  rate: number;
  classification: 'Standard' | 'Intermediate' | 'Expert';
  effectiveFrom?: string;
  effectiveTo?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private getApiUrl(facilityId: string, path: string) {
    return `/api/facilities/${facilityId}/${path}`;
  }

  constructor(private http: HttpClient) {}

  // Pay Period Methods
  getPayPeriod(facilityId: string): Observable<PayPeriod> {
    return this.http.get<PayPeriod>(this.getApiUrl(facilityId, 'pay-period'));
  }

  updatePayPeriod(
    facilityId: string,
    data: Partial<PayPeriod>
  ): Observable<PayPeriod> {
    return this.http.put<PayPeriod>(
      this.getApiUrl(facilityId, 'pay-period'),
      data
    );
  }

  // Attendance Settings Methods
  getAttendanceSettings(
    facilityId: string
  ): Observable<FacilityAttendanceSettingsDto> {
    return this.http.get<FacilityAttendanceSettingsDto>(
      this.getApiUrl(facilityId, 'attendance-settings')
    );
  }

  updateAttendanceSettings(
    facilityId: string,
    data: UpdateFacilityAttendanceSettingsDto
  ): Observable<FacilityAttendanceSettingsDto> {
    return this.http.put<FacilityAttendanceSettingsDto>(
      this.getApiUrl(facilityId, 'attendance-settings'),
      data
    );
  }

  // Shift Template Methods
  getShiftTemplates(facilityId: string): Observable<ShiftTemplate[]> {
    return this.http.get<ShiftTemplate[]>(
      this.getApiUrl(facilityId, 'shift-templates')
    );
  }

  addShiftTemplate(
    facilityId: string,
    data: Partial<ShiftTemplate>
  ): Observable<ShiftTemplate> {
    return this.http.post<ShiftTemplate>(
      this.getApiUrl(facilityId, 'shift-templates'),
      data
    );
  }

  updateShiftTemplate(
    facilityId: string,
    templateId: string,
    data: Partial<ShiftTemplate>
  ): Observable<ShiftTemplate> {
    return this.http.put<ShiftTemplate>(
      this.getApiUrl(facilityId, `shift-templates/${templateId}`),
      data
    );
  }

  deleteShiftTemplate(
    facilityId: string,
    templateId: string
  ): Observable<void> {
    return this.http.delete<void>(
      this.getApiUrl(facilityId, `shift-templates/${templateId}`)
    );
  }

  // Billable Rates Methods
  getBillableRates(facilityId: string): Observable<BillableRate[]> {
    return this.http.get<BillableRate[]>(
      this.getApiUrl(facilityId, 'billable-rates')
    );
  }

  saveBillableRate(
    facilityId: string,
    data: BillableRate
  ): Observable<BillableRate> {
    return this.http.post<BillableRate>(
      this.getApiUrl(facilityId, 'billable-rates'),
      data
    );
  }

  scheduleBillableRate(
    facilityId: string,
    data: BillableRate
  ): Observable<BillableRate> {
    return this.http.post<BillableRate>(
      this.getApiUrl(facilityId, 'billable-rates/schedule'),
      data
    );
  }
}
